import type { DynamicFilter, DynamicSort } from "../types";
import { AttributeDataType, FilterOperator } from "../types";

/**
 * Legacy filter format for backward compatibility with GetPpsJobOrdersDynamic backend
 */
interface LegacyFilter {
  Field: string;
  DataType: number;
  Operator: number;
  Value: any;
  NestedField?: string;
}

/**
 * Legacy sort format for backward compatibility
 */
interface LegacySort {
  Field: string;
  Direction: number;
  NestedField?: string;
}

/**
 * Map type string to FilterOperator enum
 */
function mapTypeToOperator(type: string): FilterOperator {
  switch (type.toLowerCase()) {
    case "is":
      return FilterOperator.IS;
    case "is not":
      return FilterOperator.IS_NOT;
    case "is empty":
    case "is not empty":
      return FilterOperator.IS_EMPTY;
    case "contains":
      return FilterOperator.CONTAINS;
    case "start with":
    case "starts with":
      return FilterOperator.STARTS_WITH;
    case "end with":
    case "ends with":
      return FilterOperator.ENDS_WITH;
    case "range":
      return FilterOperator.RANGE;
    default:
      return FilterOperator.CONTAINS;
  }
}

/**
 * Serialize filters to legacy format for GetPpsJobOrdersDynamic
 */
export function serializeFiltersToLegacyFormat(
  filters: DynamicFilter[],
  attributeDataTypeMap: Record<string, AttributeDataType> = {}
): string | undefined {
  if (!filters || filters.length === 0) return undefined;

  const legacyFilters: LegacyFilter[] = filters
    .filter((f) => {
      // Filter must have filterBy field
      if (!f.filterBy) return false;

      // For range filters, check if value has from/to
      if (f.type.toLowerCase() === "range") {
        if (typeof f.value === "object" && f.value !== null) {
          return f.value.from || f.value.to;
        }
        return false;
      }

      // For other filters, value must not be empty
      return f.value !== null && f.value !== "" && f.value !== undefined;
    })
    .map((filter) => ({
      Field: filter.filterBy,
      DataType: attributeDataTypeMap[filter.filterBy] || AttributeDataType.TEXT,
      Operator: mapTypeToOperator(filter.type),
      Value: filter.value,
      NestedField: "", // Will be set by backend if needed
    }));

  return legacyFilters.length > 0 ? JSON.stringify(legacyFilters) : undefined;
}

/**
 * Serialize sorts to legacy format
 */
export function serializeSortsToLegacyFormat(
  sorts: DynamicSort[]
): string | undefined {
  if (!sorts || sorts.length === 0) return undefined;

  const legacySorts: LegacySort[] = sorts
    .filter((s) => s.field)
    .map((sort) => ({
      Field: sort.field,
      Direction: sort.direction,
      NestedField: sort.nestedField || "",
    }));

  return legacySorts.length > 0 ? JSON.stringify(legacySorts) : undefined;
}
